/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Multi-Gateway Payment Checkout Modal (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  CreditCard,
  Wallet,
  ShieldCheck,
  Building2,
  X,
  Check,
  Copy,
  Upload,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useUiStore } from '../../stores/useUiStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { useWalletBalance, formatCreditNumber } from '../../hooks/useWalletBalance';
import { getApiBaseUrl } from '../../lib/api-client';
import { openPaddleOverlay } from '../../lib/paddle';
import type {
  UniversalPaymentGateway,
  UniversalCheckoutResponse,
} from '@sofia/shared-types';

export interface PaymentCheckoutPayload {
  itemType: 'subscription' | 'token_pack';
  itemId: string;
  name: string;
  credits: number;
  amount: number;
  currency?: string;
  billingInterval?: 'monthly' | 'yearly';
}

export const PaymentCheckoutModal: React.FC = () => {
  const { t } = useI18nStore();
  const { activeModal, modalPayload, closeModal } = useUiStore();
  const { refetchWallet } = useWalletBalance();

  const isOpen = activeModal === 'payment_checkout';
  const payload = modalPayload as PaymentCheckoutPayload | null;

  const [selectedGateway, setSelectedGateway] = useState<UniversalPaymentGateway>('stripe');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [orderResult, setOrderResult] = useState<UniversalCheckoutResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedMemo, setCopiedMemo] = useState<boolean>(false);
  const [isUploadingProof, setIsUploadingProof] = useState<boolean>(false);
  const [proofUploaded, setProofUploaded] = useState<boolean>(false);

  if (!isOpen || !payload) {
    return null;
  }

  const handleClose = () => {
    setIsProcessing(false);
    setOrderResult(null);
    setErrorMessage(null);
    setCopiedMemo(false);
    setProofUploaded(false);
    closeModal();
  };

  const handleCopyMemo = async (memo: string) => {
    try {
      await navigator.clipboard.writeText(memo);
      setCopiedMemo(true);
      setTimeout(() => setCopiedMemo(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleProceedPayment = async () => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/v1/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Workspace-Id': 'ws_demo_primary',
        },
        body: JSON.stringify({
          planId: payload.itemId,
          gateway: selectedGateway,
          billingInterval: payload.billingInterval || 'monthly',
          topupCredits: payload.itemType === 'token_pack' ? payload.credits : undefined,
          amount: payload.amount,
          currency: payload.currency || 'USD',
          successUrl: `${window.location.origin}/workspace/subscription?success=true`,
          cancelUrl: `${window.location.origin}/workspace/subscription?canceled=true`,
        }),
      });

      if (!res.ok) {
        const errorData = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errorData.message || 'Failed to initialize checkout order');
      }

      const json = (await res.json()) as { success: boolean; data: UniversalCheckoutResponse };
      const data = json.data;
      setOrderResult(data);

      // Handle Gateway-Specific Completion
      if (selectedGateway === 'stripe' && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      if (selectedGateway === 'paypal' && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      if (selectedGateway === 'paddle') {
        if (data.paddleTransactionId) {
          const opened = await openPaddleOverlay({
            transactionId: data.paddleTransactionId,
            successUrl: `${window.location.origin}/workspace/subscription?success=true`,
          });
          if (!opened && data.checkoutUrl) {
            window.location.href = data.checkoutUrl;
          }
        } else if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        }
        return;
      }

      // If Bank Transfer, orderResult is displayed in-modal with VietQR
      setIsProcessing(false);
    } catch (err: unknown) {
      setIsProcessing(false);
      const msg = err instanceof Error ? err.message : 'Error processing checkout';
      setErrorMessage(msg);
    }
  };

  const handleUploadProof = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !orderResult) return;

    setIsUploadingProof(true);
    // Simulate R2 presigned upload
    setTimeout(async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const mockR2Url = `https://r2.sophiaplatform.com/proofs/${orderResult.orderId}-${file.name}`;
        await fetch(`${baseUrl}/api/v1/billing/orders/${orderResult.orderId}/receipt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proofFileUrl: mockR2Url }),
        }).catch(() => null);

        setIsUploadingProof(false);
        setProofUploaded(true);
        void refetchWallet();
      } catch {
        setIsUploadingProof(false);
        setProofUploaded(true);
      }
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-3xl bg-surface border border-surface-border shadow-2xl p-6 space-y-6">
        {/* Header with Title and Close Button */}
        <div className="flex items-center justify-between pb-4 border-b border-surface-border">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <CreditCard className="size-5" />
            </div>
            <div>
              <h2 className="text-base font-bold font-heading text-foreground">
                {t('checkout.title')}
              </h2>
              <p className="text-xs text-foreground-muted">
                {t('checkout.subtitle')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="size-8 rounded-xl flex items-center justify-center text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="flex items-start gap-2.5 p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* 1. Order Summary Card */}
        <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
            {t('checkout.orderSummary')}
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-sm font-bold text-foreground">{payload.name}</div>
              <div className="flex items-center gap-2 text-xs text-foreground-muted">
                <span className="font-medium text-primary">
                  {t('checkout.creditsIncluded', { count: formatCreditNumber(payload.credits) })}
                </span>
                <span>•</span>
                <span>
                  {payload.itemType === 'subscription'
                    ? payload.billingInterval === 'yearly'
                      ? t('checkout.billedYearly')
                      : t('checkout.billedMonthly')
                    : t('checkout.oneTimePayment')}
                </span>
              </div>
            </div>

            <div className="text-right">
              <div className="font-mono text-xl font-bold text-foreground">
                ${payload.amount.toFixed(2)}
              </div>
              <div className="text-[10px] text-foreground-muted font-mono uppercase">
                {payload.currency || 'USD'}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Bank Transfer Result Screen (If generated) */}
        {orderResult?.gateway === 'bank_transfer' && orderResult.bankDetails ? (
          <div className="p-4 rounded-2xl bg-cyan-500/5 border border-cyan-500/20 space-y-4 animate-in fade-in">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                <Building2 className="size-4 text-cyan-500" />
                <span>{t('checkout.bankWire.title')}</span>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-cyan-500/15 text-cyan-600 dark:text-cyan-400">
                {orderResult.orderId}
              </span>
            </div>

            {/* QR Code and Bank Details Grid */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-1">
              {/* VietQR Dynamic Code */}
              <div className="size-36 rounded-xl border border-surface-border bg-white p-2 flex items-center justify-center shrink-0 shadow-xs">
                <img
                  src={orderResult.bankDetails.qrUrl}
                  alt={t('checkout.bankWire.scanQr')}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Wire Instructions Information */}
              <div className="space-y-2 text-xs text-foreground w-full">
                <div className="flex items-center justify-between py-1 border-b border-surface-border/60">
                  <span className="text-foreground-muted">{t('checkout.bankWire.bankName')}:</span>
                  <span className="font-semibold">{orderResult.bankDetails.bankName}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-surface-border/60">
                  <span className="text-foreground-muted">{t('checkout.bankWire.accountNumber')}:</span>
                  <span className="font-mono font-bold">{orderResult.bankDetails.accountNumber}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-surface-border/60">
                  <span className="text-foreground-muted">{t('checkout.bankWire.accountHolder')}:</span>
                  <span className="font-semibold">{orderResult.bankDetails.accountHolder}</span>
                </div>

                <div className="flex items-center justify-between py-1 border-b border-surface-border/60">
                  <span className="text-foreground-muted">{t('checkout.bankWire.amount')}:</span>
                  <span className="font-mono font-bold text-primary">
                    {orderResult.bankDetails.amountVnd.toLocaleString()} VND
                  </span>
                </div>

                {/* Transfer Memo with One-Click Copy */}
                <div className="flex items-center justify-between py-1">
                  <span className="text-foreground-muted">{t('checkout.bankWire.transferCode')}:</span>
                  <button
                    type="button"
                    onClick={() => handleCopyMemo(orderResult.bankDetails!.transferCode)}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-mono font-bold hover:bg-primary/20 transition-colors"
                  >
                    <span>{orderResult.bankDetails.transferCode}</span>
                    {copiedMemo ? <Check className="size-3 text-emerald-500" /> : <Copy className="size-3" />}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-500/10 p-2.5 rounded-xl border border-amber-500/20">
              {t('checkout.bankWire.importantNote')}
            </p>

            {/* Receipt Screenshot Upload */}
            <div className="pt-2">
              {proofUploaded ? (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs">
                  <CheckCircle2 className="size-4 shrink-0" />
                  <span>{t('checkout.bankWire.receiptUploaded')}</span>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary cursor-pointer text-xs font-semibold transition-colors">
                  {isUploadingProof ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      <span>{t('checkout.bankWire.uploading')}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="size-4" />
                      <span>{t('checkout.bankWire.uploadReceipt')}</span>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleUploadProof}
                        className="hidden"
                      />
                    </>
                  )}
                </label>
              )}
            </div>
          </div>
        ) : (
          /* 3. Payment Gateway Selector Cards */
          <div className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
              {t('checkout.selectGateway')}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {/* Option 1: Stripe */}
              <button
                type="button"
                onClick={() => setSelectedGateway('stripe')}
                className={`flex items-start gap-3 p-3.5 rounded-2xl border text-left transition-all ${
                  selectedGateway === 'stripe'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-xs'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated'
                }`}
              >
                <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <CreditCard className="size-4" />
                </div>
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground">
                      {t('checkout.gateways.stripe')}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">
                      {t('checkout.gateways.stripeBadge')}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-tight">
                    {t('checkout.gateways.stripeDesc')}
                  </p>
                </div>
              </button>

              {/* Option 2: PayPal */}
              <button
                type="button"
                onClick={() => setSelectedGateway('paypal')}
                className={`flex items-start gap-3 p-3.5 rounded-2xl border text-left transition-all ${
                  selectedGateway === 'paypal'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-xs'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated'
                }`}
              >
                <div className="size-8 rounded-xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center shrink-0 mt-0.5">
                  <Wallet className="size-4" />
                </div>
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground">
                      {t('checkout.gateways.paypal')}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
                      {t('checkout.gateways.paypalBadge')}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-tight">
                    {t('checkout.gateways.paypalDesc')}
                  </p>
                </div>
              </button>

              {/* Option 3: Paddle Billing (MoR) */}
              <button
                type="button"
                onClick={() => setSelectedGateway('paddle')}
                className={`flex items-start gap-3 p-3.5 rounded-2xl border text-left transition-all ${
                  selectedGateway === 'paddle'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-xs'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated'
                }`}
              >
                <div className="size-8 rounded-xl bg-accent/10 text-accent flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldCheck className="size-4" />
                </div>
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground">
                      {t('checkout.gateways.paddle')}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-accent/10 text-accent">
                      {t('checkout.gateways.paddleBadge')}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-tight">
                    {t('checkout.gateways.paddleDesc')}
                  </p>
                </div>
              </button>

              {/* Option 4: Bank Transfer / VietQR */}
              <button
                type="button"
                onClick={() => setSelectedGateway('bank_transfer')}
                className={`flex items-start gap-3 p-3.5 rounded-2xl border text-left transition-all ${
                  selectedGateway === 'bank_transfer'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30 shadow-xs'
                    : 'border-surface-border bg-surface hover:bg-surface-elevated'
                }`}
              >
                <div className="size-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                  <Building2 className="size-4" />
                </div>
                <div className="space-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-foreground">
                      {t('checkout.gateways.bankTransfer')}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-500">
                      {t('checkout.gateways.bankTransferBadge')}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-muted leading-tight">
                    {t('checkout.gateways.bankTransferDesc')}
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Security Certificate Badge */}
        <div className="flex items-center justify-center gap-2 text-[10px] text-foreground-muted pt-2 border-t border-surface-border/50">
          <ShieldCheck className="size-3.5 text-primary shrink-0" />
          <span>{t('checkout.secureBadge')}</span>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 rounded-xl border border-surface-border text-foreground hover:bg-surface-elevated text-xs font-semibold transition-colors"
          >
            {t('checkout.cancel')}
          </button>

          {(!orderResult || orderResult.gateway !== 'bank_transfer') && (
            <button
              type="button"
              disabled={isProcessing}
              onClick={handleProceedPayment}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-fg hover:opacity-90 active:scale-98 text-xs font-semibold transition-all shadow-xs disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  <span>{t('checkout.processing')}</span>
                </>
              ) : (
                <>
                  <span>
                    {t('checkout.btnProceed', {
                      gateway:
                        selectedGateway === 'stripe'
                          ? 'Stripe'
                          : selectedGateway === 'paypal'
                            ? 'PayPal'
                            : selectedGateway === 'paddle'
                              ? 'Paddle'
                              : 'VietQR',
                    })}
                  </span>
                  <ArrowRight className="size-3.5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
